/**
 * App-shell ("chrome") colour tokens per theme mode — the AppBar, the sidebar
 * drawer, its nav items, and the main content surface. Layout / NavItem /
 * GlobalSearch read these instead of branching on `themeMode` inline, so a new
 * theme is one entry here plus its MUI theme in `themes.ts`.
 *
 * `dark` means the chrome is a dark surface with white text (orange, dark and
 * slate all are); light keeps the MUI defaults (`inherit`).
 */
export interface ChromeTokens {
  dark: boolean;
  /** Solid AppBar / sidebar surface. */
  bg: string;
  /** Drawer paper (may be translucent so the weather overlay shows through). */
  drawerBg: string;
  /** Hover tint for icon buttons on the chrome. */
  hover: string;
  /** Nav item hover tint. */
  navHover: string;
  /** Selected nav item. */
  navSelectedBg: string;
  navSelectedHoverBg: string;
  navSelectedShadow: string;
  /** Text/icon colour on a selected nav item while hovered. */
  navSelectedHoverFg: string;
  navRadius: string;
  /** Hairline between sidebar sections. */
  divider: string;
  /** Override for the Divider under the sidebar toolbar (unset = palette default). */
  toolbarDivider?: string;
  /** Accent for shell affordances (theme picker selection, profile-row hover). */
  accent: string;
  /** Main content background (`inherit` = theme default). */
  mainBg: string;
  /** Whether the chrome goes glassy (frosted / see-through) over the weather overlay. */
  weatherGlass: boolean;
}

const NAVY = '#1a2233';

const orange: ChromeTokens = {
  dark: true,
  bg: NAVY,
  drawerBg: 'rgba(26, 34, 51, 0.7)',
  hover: 'rgba(255, 140, 0, 0.1)',
  navHover: 'rgba(255, 255, 255, 0.08)',
  navSelectedBg: '#ff8c00',
  navSelectedHoverBg: '#f57c00',
  navSelectedShadow: '0 6px 20px rgba(255, 140, 0, 0.4)',
  navSelectedHoverFg: '#000000',
  navRadius: '0 20px 20px 0',
  divider: 'rgba(255,255,255,0.06)',
  accent: '#ff8c00',
  mainBg: '#f8f9fb',
  weatherGlass: true,
};

const dark: ChromeTokens = {
  ...orange,
  navSelectedShadow: 'none',
  navSelectedHoverFg: 'inherit',
  mainBg: '#0a0e17',
};

/**
 * Slate — the "enterprise workbench" look: deep navy chrome, a quiet
 * rectangular selected row, white content, no glow / wave effects.
 */
export const SLATE = {
  navy: '#0f2a44',
  navyRaised: '#1b3a5c',
  navyRaisedHover: '#22476f',
  accent: '#1a56db',
  border: '#e5e7eb',
  surface: '#ffffff',
  surfaceMuted: '#f8fafc',
  text: '#111827',
  textMuted: '#6b7280',
} as const;

const slate: ChromeTokens = {
  dark: true,
  bg: SLATE.navy,
  drawerBg: SLATE.navy,
  hover: 'rgba(255, 255, 255, 0.1)',
  navHover: 'rgba(255, 255, 255, 0.06)',
  navSelectedBg: SLATE.navyRaised,
  navSelectedHoverBg: SLATE.navyRaisedHover,
  navSelectedShadow: 'none',
  navSelectedHoverFg: '#ffffff',
  navRadius: '6px',
  divider: 'rgba(255,255,255,0.08)',
  toolbarDivider: 'rgba(255,255,255,0.08)',
  accent: SLATE.accent,
  mainBg: SLATE.surface,
  weatherGlass: false,
};

const light: ChromeTokens = {
  dark: false,
  bg: 'inherit',
  drawerBg: 'rgba(255, 255, 255, 0.7)',
  hover: 'rgba(255, 255, 255, 0.1)',
  navHover: 'inherit',
  navSelectedBg: 'inherit',
  navSelectedHoverBg: 'inherit',
  navSelectedShadow: 'none',
  navSelectedHoverFg: 'inherit',
  navRadius: 'inherit',
  divider: 'rgba(0,0,0,0.06)',
  accent: '#ff8c00',
  mainBg: 'inherit',
  weatherGlass: true,
};

export const getChrome = (mode: string): ChromeTokens => {
  switch (mode) {
    case 'orange':
      return orange;
    case 'dark':
      return dark;
    case 'slate':
      return slate;
    default:
      return light;
  }
};
